import { useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  LeaseChargeType,
  LeaseChargeTypeIsDueNow,
  LeaseChargeTypeIsRecurring,
  LeaseChargeTypeToDisplayText,
  PageType,
  PropertySettingType,
} from '@common/entities';
import { isCancel } from 'axios';
import { NotificationStatus } from 'enums/notification-status';
import { enqueueNotification, NotificationPayload } from 'state/slices/notificationsSlice';
import {
  CartCharge,
  CartChargeType,
  resetCartCharges,
  setIsSyncingCharges,
  setScheduledChargesNotApplicable,
  setScheduledChargesSyncFailure,
  setScheduledChargesSyncStart,
  setScheduledChargesSyncSuccess,
} from 'state/slices/shoppingCartSlice';
import { RootState } from 'state/store';

import { usePropertySettingEnabled } from 'hooks/usePropertySettingEnabled';
import { syncScheduledCharges } from 'services/scheduled-charges';
import { filterScheduledChargesForApplicant } from 'utils/filterScheduledChargesForApplicant';
import { addInstallmentNumbers, isInstallmentCharge } from 'utils/installment-charges';
import { parseCartTaxAmountFromScheduledCharge } from 'utils/parseCartTaxAmountFromScheduledCharge';

// A charge refresh must finish even if the component that started it unmounts behind a page loader.
// A newer refresh still cancels the previous request.
let activeSyncController: AbortController | null = null;

function isRequestAborted(error: unknown): boolean {
  if (isCancel(error)) return true;
  if (error !== null && typeof error === 'object') {
    const { code, name } = error as { code?: string; name?: string };
    return code === 'ERR_CANCELED' || name === 'AbortError' || name === 'CanceledError';
  }
  return false;
}

// Map notification type to status
const mapNotificationTypeToStatus = (type: string): NotificationStatus => {
  switch (type) {
    case 'SCHEDULED_CHARGE_CHANGE':
      return NotificationStatus.INFO;
    default:
      return NotificationStatus.INFO;
  }
};

/**
 * Gets the display name for a charge from all_charges_details.
 * Format: "ar_code_name (ar_origin_reference_name)" if both exist,
 * otherwise falls back to ar_code_name.
 */
export function getChargeDisplayName(charge: any): string {
  // Check if allChargesDetails exists and has at least one item
  if (
    charge.allChargesDetails &&
    Array.isArray(charge.allChargesDetails) &&
    charge.allChargesDetails.length > 0
  ) {
    const firstDetail = charge.allChargesDetails[0];
    const arCodeName = firstDetail.arCodeName?.trim();
    const arOriginReferenceName = firstDetail.arOriginReferenceName?.trim();

    // Only use the charge-code format if both detail fields exist.
    if (arCodeName && arOriginReferenceName) {
      return `${arCodeName} (${arOriginReferenceName})`;
    }
  }

  // Prefer the first detail's ar_code_name when the reference name is unavailable.
  const firstDetail = charge.allChargesDetails?.[0];
  return firstDetail?.arCodeName?.trim() || charge.arCodeName || '';
}

interface Props {
  propertyId: number;
  leaseId: number;
  leaseIntervalId?: number;
}

function useRefreshScheduledCharges({ propertyId, leaseId, leaseIntervalId }: Props) {
  const dispatch = useDispatch();
  const store = useStore();
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  const displayOptionalCustomFees = usePropertySettingEnabled(
    PropertySettingType.DisplayOptionalCustomFees,
  );

  // Get applicationResponseId from Redux state
  const { responseId: applicationResponseId, relationshipType } = useSelector(
    (state: RootState) => state.application.applicant?.currentResponse,
  );
  const { customerId } = useSelector((state: RootState) => state.application.applicant);
  const hasPaymentPage = useSelector((state: RootState) =>
    state.application.form.pages.some((page) => page.type === PageType.Payment),
  );

  async function refreshScheduledCharges(isManualRetry = false) {
    if (!leaseId && !hasPaymentPage) {
      activeSyncController?.abort();
      activeSyncController = null;
      dispatch(setScheduledChargesNotApplicable());
      return;
    }

    if (!propertyId || !leaseId || !applicationResponseId) {
      // eslint-disable-next-line no-console
      console.error('Cannot sync scheduled charges: required application identifiers are missing');
      activeSyncController?.abort();
      activeSyncController = null;
      dispatch(setScheduledChargesSyncFailure(isManualRetry));
      return;
    }

    activeSyncController?.abort();
    const controller = new AbortController();
    activeSyncController = controller;
    const isCurrentSession = () =>
      (store.getState() as RootState).application.applicant?.currentResponse?.responseId ===
      applicationResponseId;
    const unsubscribe = store.subscribe(() => {
      if (!isCurrentSession()) controller.abort();
    });
    const canApplyResult = () =>
      activeSyncController === controller && !controller.signal.aborted && isCurrentSession();

    dispatch(setScheduledChargesSyncStart());
    let hasSyncSucceeded = false;
    let hasSyncFailed = false;
    try {
      // eslint-disable-next-line no-console
      console.log(
        `Syncing scheduled charges for applicationResponseId: ${applicationResponseId}, propertyId: ${propertyId}, leaseId: ${leaseId}`,
      );

      // Use new sync endpoint that returns charges AND notifications
      const { data } = await syncScheduledCharges(
        propertyId,
        leaseId,
        applicationResponseId,
        leaseIntervalId,
        controller.signal,
      );
      if (!canApplyResult()) return;

      // An empty charge list is a valid, successfully loaded result.
      const baseCharges: CartCharge[] = filterScheduledChargesForApplicant(
        data?.charges ?? [],
        customerId,
        relationshipType,
        displayOptionalCustomFees,
      ).map((charge: any) => {
        return {
          id: charge.summarizationGroupId,
          type: CartChargeType.Rent,
          label: getChargeDisplayName(charge),
          arTriggerId: charge.arTriggerId,
          priceNumber: charge.chargeAmount.amountNumber,
          priceFormatted: charge.chargeAmount.amountFormatted,
          currencyCode: charge.chargeAmount.currencyCode,
          dueNow: isInstallmentCharge(charge)
            ? typeof charge.dueNowAmount === 'number' && charge.dueNowAmount !== 0
            : LeaseChargeTypeIsDueNow.has(charge.arTriggerId),
          dueAtMoveIn:
            charge.arTriggerId === LeaseChargeType.MoveIn ||
            charge.arTriggerId === LeaseChargeType.ApplicationApproval,
          isRecurring: LeaseChargeTypeIsRecurring.has(charge.arTriggerId),
          recurringFrequency: LeaseChargeTypeToDisplayText(charge.arTriggerId),
          isRange: charge.chargeAmount.isRange,
          minPriceNumber: charge.chargeAmount.minAmountNumber,
          maxPriceNumber: charge.chargeAmount.maxAmountNumber,
          minPriceFormatted: charge.chargeAmount.minAmountFormatted,
          maxPriceFormatted: charge.chargeAmount.maxAmountFormatted,
          amountType: charge.amountType,
          customFeeAmountType: charge.chargeAmount.customFeeAmountType,
          amountDetail: charge.chargeAmount.amountDetail,
          allChargesDetails: charge.allChargesDetails,
          taxAmount: parseCartTaxAmountFromScheduledCharge(charge.taxAmount),
          arCodeName: charge.arCodeName,
          installmentId: charge.installmentId,
          chargeStartDate: charge.chargeStartDate,
          chargeEndDate: charge.chargeEndDate,
          totalValueWithTax: charge.totalValueWithTax,
          dueNowAmount: charge.dueNowAmount,
        };
      });
      const charges = addInstallmentNumbers(baseCharges);
      dispatch(resetCartCharges(charges));

      // Dispatch notifications immediately (no setTimeout needed!)
      if (data?.notifications && data.notifications.length > 0) {
        // eslint-disable-next-line no-console
        console.log(`Dispatching ${data.notifications.length} charge change notifications`);

        data.notifications.forEach((notification) => {
          const notificationPayload: NotificationPayload = {
            id: notification.id,
            title: notification.title,
            message: notification.message,
            status: mapNotificationTypeToStatus(notification.type),
            date: notification.createdDate
              ? new Date(notification.createdDate).toISOString()
              : new Date().toISOString(),
            persist: true,
            read: !!notification.readDate, // Check readDate to determine if already read
            type: notification.type,
            metadata: notification.metadata,
          };
          dispatch(enqueueNotification(notificationPayload));
        });
      }
      hasSyncSucceeded = true;
    } catch (error) {
      if (isRequestAborted(error) || !canApplyResult()) return;
      // eslint-disable-next-line no-console
      console.error('Failed to sync scheduled charges:', error);
      // Still proceed with empty charges to avoid blocking UI
      dispatch(resetCartCharges([]));
      hasSyncFailed = true;
    } finally {
      unsubscribe();
      if (activeSyncController === controller) {
        activeSyncController = null;
        if (isCurrentSession() && !controller.signal.aborted) {
          if (hasSyncSucceeded) {
            dispatch(setScheduledChargesSyncSuccess());
          } else if (hasSyncFailed) {
            dispatch(setScheduledChargesSyncFailure(isManualRetry));
          } else {
            dispatch(setIsSyncingCharges(false));
          }
          setLastRefreshTime(Date.now());
        }
      }
    }
  }

  return { refreshScheduledCharges, lastRefreshTime };
}

export default useRefreshScheduledCharges;
